import { tv } from 'tailwind-variants'

import { typography } from '@gto/ui/typography'

export const checkboxVariants = tv({
  slots: {
    root: [
      'group/cb',
      'relative inline-flex shrink-0 cursor-pointer items-center justify-center',
      'box-border rounded-xs border-2 border-solid',
      'appearance-none m-0 bg-transparent p-0',
      'transition-[background-color,border-color,box-shadow] duration-150',
      'outline-none',
      'data-[focus-visible]:outline data-[focus-visible]:outline-1 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-primary-main',
      'data-[disabled]:cursor-not-allowed data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      'data-[unchecked]:border-text-action-active',
      'data-[indeterminate]:border-transparent data-[indeterminate]:bg-secondary-dark',
      '[&[data-indeterminate][data-focus-visible]]:outline-none',
    ],
    indicator: ['pointer-events-none inline-flex items-center justify-center'],
    checkIcon: ['block group-data-[indeterminate]/cb:hidden'],
    indeterminateIcon: ['block group-data-[checked]/cb:hidden text-text-disabled'],
    label: ['inline-flex cursor-pointer items-center gap-2'],
    labelText: ['text-sm text-text-primary'],
    iconRoot: [
      'group/cb inline-flex shrink-0 cursor-pointer items-center justify-center',
      'appearance-none m-0 border-0 bg-transparent p-0 outline-none',
      'transition-colors duration-150',
      '[&>svg]:size-full',
      'data-[focus-visible]:outline data-[focus-visible]:outline-1 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-primary-main',
      'data-[disabled]:cursor-not-allowed data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      'data-[unchecked]:text-text-disabled',
      'data-[checked]:text-primary-main data-[indeterminate]:text-primary-main',
      'data-[unchecked]:hover:text-primary-main',
      'data-[checked]:active:text-primary-dark data-[indeterminate]:active:text-primary-dark',
    ],
  },
  variants: {
    size: {
      small: {
        root: 'size-4',
        indicator: 'size-[10px]',
        checkIcon: 'size-[10px]',
        indeterminateIcon: 'size-[10px]',
        iconRoot: 'size-4',
      },
      medium: {
        root: 'size-[18px]',
        indicator: 'size-[11px]',
        checkIcon: 'size-[11px]',
        indeterminateIcon: 'size-[11px]',
        iconRoot: 'size-[18px]',
      },
      large: {
        root: 'size-5',
        indicator: 'size-[11px]',
        checkIcon: 'size-[11px]',
        indeterminateIcon: 'size-[11px]',
        iconRoot: 'size-5',
        // The only size rung that scales the LABEL too — touch surfaces need the
        // 16px text the rest of the mobile drawer uses, not the 14px default.
        labelText: [typography['input/value/medium']],
      },
    },
    color: {
      default: {},
      primary: {},
    },
    labelPlacement: {
      end: { label: 'h-10' },
      start: { label: 'h-10 flex-row-reverse' },
      top: { label: 'flex-col-reverse' },
      bottom: { label: 'flex-col' },
    },
    disabled: {
      true: {
        label: 'pointer-events-none cursor-not-allowed',
        labelText: 'opacity-50',
      },
    },
  },
  compoundVariants: [
    {
      color: 'default',
      className: {
        root: [
          'data-[unchecked]:hover:border-text-secondary data-[unchecked]:hover:shadow-glow-checkbox-default',
          'data-[checked]:border-primary-main data-[checked]:bg-primary-main',
          'data-[checked]:hover:shadow-glow-checkbox-primary',
          'data-[checked]:data-[focus-visible]:bg-primary-dark data-[checked]:data-[focus-visible]:border-primary-dark',
          'data-[checked]:active:bg-primary-dark data-[checked]:active:border-primary-dark',
          'data-[checked]:data-[disabled]:bg-[#FFFFFF2E] data-[checked]:data-[disabled]:border-transparent data-[checked]:data-[disabled]:opacity-100',
        ],
        checkIcon: 'text-primary-contrastText',
      },
    },
    {
      color: 'primary',
      className: {
        root: [
          'data-[unchecked]:hover:border-primary-dark data-[unchecked]:hover:shadow-glow-checkbox-primary',
          'data-[checked]:border-primary-main data-[checked]:bg-primary-main',
          'data-[checked]:hover:shadow-glow-checkbox-primary',
          'data-[checked]:data-[focus-visible]:bg-primary-dark data-[checked]:data-[focus-visible]:border-primary-dark',
          'data-[checked]:active:bg-primary-dark data-[checked]:active:border-primary-dark',
          'data-[checked]:data-[disabled]:bg-[#FFFFFF2E] data-[checked]:data-[disabled]:border-transparent data-[checked]:data-[disabled]:opacity-100',
        ],
        checkIcon: 'text-primary-contrastText',
      },
    },
  ],
  defaultVariants: {
    size: 'medium',
    color: 'primary',
  },
})

export type CheckboxVariants = Parameters<typeof checkboxVariants>[0]
